// src/components/inspectionBot/TemplateViewerContent.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const TemplateViewerContent = ({ isDarkMode, onNext, onBack, currentStep }) => {
  const navigate = useNavigate();
  
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Separate useEffect for fetching templates
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-template-names`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.status === 401) {
        setErrorMessage('Your session has expired. Please sign in again.');
        localStorage.removeItem('token');
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        throw new Error(data?.message || `HTTP error! status: ${response.status}`);
      }

      if (data?.success) {
        const templateList = data?.result?.TemplateNamesList || [];
        const parsedTemplates = Array.isArray(templateList) ? templateList : [];
        setTemplates(parsedTemplates);

        if (parsedTemplates.length === 0) {
          setErrorMessage('No templates found in the system.');
        }
      } else {
        setErrorMessage(data?.message || 'Failed to retrieve templates from server.');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setErrorMessage(err.message || 'Failed to fetch templates. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setErrorMessage('');
  };

  const handleNextClick = () => {
    if (!selectedTemplate) {
      setErrorMessage('Please select a template before proceeding.');
      return;
    }

    localStorage.setItem('selectedTemplate', selectedTemplate.name);
    if (onNext) {
      onNext();
    }
  };

  return (
    <div className="grid grid-cols-[30%_70%] gap-8 h-full py-4">
      
      {/* Template Selection Section */}
      <div className={`rounded-xl border shadow-lg overflow-hidden flex flex-col transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#DBDCED]'
      }`}>
        <div className={`px-5 py-5 border-b transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-[#F5F5FA] border-[#DBDCED]'
        }`}>
          <h2 className={`text-lg font-semibold mb-1.5 ${isDarkMode ? 'text-white' : 'text-[#100E27]'}`}>
            Select which communication template to send
          </h2>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Loading templates...
                </div>
              </div>
            </div>
          ) : templates.length > 0 ? (
            templates.map((template, index) => (
              <div
                key={`${template.name}-${index}`}
                onClick={() => handleTemplateSelect(template)}
                className={`px-5 py-4 cursor-pointer transition-all duration-200 border-b last:border-b-0 relative ${
                  selectedTemplate?.name === template.name
                    ? isDarkMode 
                      ? 'bg-[#7376B8] text-white' 
                      : 'bg-[#100E27] text-white'
                    : isDarkMode
                      ? 'border-gray-600 hover:bg-gray-700 text-gray-300'
                      : 'border-[#DBDCED] hover:bg-[#F5F5FA] text-[#100E27]'
                }`}
              >
                {selectedTemplate?.name === template.name && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-white"></div>
                )}
                <div className="text-sm font-medium mb-1">{template.name}</div>
                {template.title && (
                  <div className={`text-xs opacity-80 leading-tight ${
                    selectedTemplate?.name === template.name ? 'text-white' : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {template.title}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className={`text-2xl mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>📄</div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No templates available
                </div>
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="p-4 m-4 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              <div className="flex items-start">
                <span className="mr-2">⚠️</span>
                <div>
                  {errorMessage}
                  <button
                    onClick={fetchTemplates}
                    className="block mt-1 text-xs text-red-700 hover:underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Section */}
      <div className={`rounded-xl border shadow-lg overflow-hidden flex flex-col transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#DBDCED]'
      }`}>
        <div className={`px-5 py-5 border-b transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-[#F5F5FA] border-[#DBDCED]'
        }`}>
          <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#100E27]'}`}>
            Preview
          </h2>
        </div>
        
        <div className="flex-1 p-5 overflow-hidden">
          {selectedTemplate ? (
            <div className="h-full flex flex-col">
              <div className={`mb-4 pb-3 border-b flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-[#DBDCED]'}`}>
                <div className={`text-base font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-[#100E27]'}`}>
                  {selectedTemplate.name}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#41425A]'}`}>
                  {selectedTemplate.title || 'Communication template'}
                </div>
              </div>
              
              {/* Scrollable Preview Content */}
              <div className={`flex-1 border rounded-lg p-4 overflow-y-auto transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-[#F5F5FA] border-[#DBDCED]'
              }`}>
                <div className={`text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-[#100E27]'}`}>
                  {/* Template preview content */}
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    isDarkMode ? 'bg-[#7376B8] text-white' : 'bg-[#100E27] text-white'
                  }`}>$First$</span>{' '}
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    isDarkMode ? 'bg-[#7376B8] text-white' : 'bg-[#100E27] text-white'
                  }`}>$Last$</span>,{'\n\n'}
                  This is a preview of the selected template. The actual content will be loaded dynamically from your system.{'\n\n'}
                  Template variables like{' '}
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    isDarkMode ? 'bg-[#7376B8] text-white' : 'bg-[#100E27] text-white'
                  }`}>$ApplicationNumber$</span>{' '}
                  will be replaced with actual values when sent.{'\n\n'}
                  This preview area now has its own scrolling capability, allowing you to view longer template content without affecting the main page layout. The content will expand as needed while keeping the overall page structure fixed.{'\n\n'}
                  Additional template variables that might be included:{'\n'}
                  • <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    isDarkMode ? 'bg-[#7376B8] text-white' : 'bg-[#100E27] text-white'
                  }`}>$PropertyAddress$</span>{'\n'}
                  • <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    isDarkMode ? 'bg-[#7376B8] text-white' : 'bg-[#100E27] text-white'
                  }`}>$ReviewDate$</span>{'\n'}
                  • <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    isDarkMode ? 'bg-[#7376B8] text-white' : 'bg-[#100E27] text-white'
                  }`}>$PermitNumber$</span>{'\n\n'}
                  Thanks,{'\n'}Agency
                </div>
              </div>
            </div>
          ) : (
            <div className={`h-full flex items-center justify-center text-center border-2 border-dashed rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'border-gray-600 text-gray-400' : 'border-[#DBDCED] text-[#41425A]'
            }`}>
              <div>
                <div className="text-4xl mb-4">📄</div>
                <div className="font-medium mb-2">Select a template from the left to preview its content</div>
                <div className="text-sm opacity-70">Choose from the available communication templates</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateViewerContent;